import { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell, COLORS,
  type DocCtx, type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type CoverageStatus = 'covered' | 'partial' | 'missing';
type CoverageFramework = 'attack' | 'defend' | 'rules' | 'procedures' | 'evidence';
type Risk = 'Low' | 'Medium' | 'High' | 'Critical';

interface AttackProcedure {
  id: string;
  techniqueId: string;
  techniqueName: string;
  tactic: string;
  ruleId: string;
  ruleName: string;
  enabled: boolean;
  severity: string;
  kind: string;
  description: string;
  querySnippet: string;
  dataSources: string[];
  entityMappings: string[];
  procedureText: string;
  lastModifiedUtc: string | null;
  recommendation: string;
}
interface RuleSummary {
  id: string;
  ruleName: string;
  enabled: boolean;
  severity: string;
  kind: string;
  tactics: string[];
  techniques: string[];
  procedures: number;
  attackCoverage: number;
  defendCoverage: number;
  alertVolume: number;
  incidentVolume: number;
  lastTriggered: string | null;
  dataSources: string[];
  gaps: string[];
  recommendation: string;
}
interface TechniqueCoverage {
  id: string;
  name: string;
  tactic: string;
  enabledRules: number;
  totalRules: number;
  alertVolume: number;
  incidentVolume: number;
  lastTriggered: string | null;
  status: CoverageStatus;
  ruleNames: string[];
  procedures: AttackProcedure[];
  defendCapabilities: string[];
  dataSources: string[];
  recommendation: string;
}
interface TacticGroup { tactic: string; covered: number; partial: number; missing: number; totalTechniques: number; alertVolume: number; techniques: TechniqueCoverage[]; }
interface DefendCapability { id: string; phase: string; name: string; description: string; mappedAttackTechniques: string[]; coveredTechniques: number; totalTechniques: number; coveragePercent: number; supportingRules: string[]; status: CoverageStatus; recommendation: string; }
interface QueryTable { id: string; title: string; columns: string[]; rows: Record<string, unknown>[]; warnings: string[]; skipped?: boolean; }
interface MitrePayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  score: { value: number; level: 'Excellent' | 'Good' | 'Needs Work' | 'Poor'; summary: string };
  cards: Array<{ id: string; label: string; value: string | number; status: Risk | 'Healthy'; helper: string }>;
  attack: TacticGroup[];
  defend: DefendCapability[];
  rules: RuleSummary[];
  procedures: AttackProcedure[];
  gaps: Array<{ id: string; tactic: string; techniqueId: string; techniqueName: string; severity: Risk; recommendation: string; defendHint: string }>;
  recommendations: Array<{ priority: Risk; title: string; details: string; owner: string }>;
  tables: QueryTable[];
  diagnostics: string[];
  summary: { totalTechniques: number; covered: number; partial: number; missing: number; totalRules: number; enabledRules: number; procedures: number; defendCapabilities: number; coveredDefendCapabilities: number };
}

interface OobAssessmentItem {
  id?: number;
  runId?: string;
  rowIndex: number;
  ruleName: string;
  ruleId: string;
  contentHubName: string;
  deployDecision: string;
  intendedEnabledState: string;
  repoPath: string;
  templateSource: string;
  assessmentAction: string;
  coverageState: string;
  readinessState: string;
  existingRuleFound: boolean;
  existingRuleId: string;
  existingRuleName: string;
  existingEnabledState: string;
  reason: string;
  rawManifestRow?: Record<string, unknown>;
}
interface OobAssessmentRun {
  ok: true;
  runId: string;
  customerId: string;
  customerName: string;
  status: string;
  sourceName: string;
  generatedAt: string;
  completedAt: string | null;
  noDeployment: boolean;
  rowCount: number;
  summary: Record<string, number>;
  diagnostics: string[];
  items: OobAssessmentItem[];
}
interface OobRunListItem { runId: string; status: string; sourceName: string; rowCount: number; summary: Record<string, number>; generatedAt: string; completedAt: string | null; }

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function statusClass(status: CoverageStatus): string {
  if (status === 'covered') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:text-emerald-100';
  if (status === 'partial') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100';
  return 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-100';
}
function statusPill(status: CoverageStatus): string {
  if (status === 'covered') return 'bg-emerald-500 text-white';
  if (status === 'partial') return 'bg-amber-400 text-slate-950';
  return 'bg-rose-500 text-white';
}
function riskPill(risk: Risk | 'Healthy'): string {
  if (risk === 'Critical') return 'bg-red-600 text-white';
  if (risk === 'High') return 'bg-orange-500 text-white';
  if (risk === 'Medium') return 'bg-amber-400 text-slate-950';
  if (risk === 'Low') return 'bg-sky-500 text-white';
  return 'bg-emerald-500 text-white';
}
function scoreGradient(score: number): string {
  if (score < 50) return 'from-rose-600 via-red-500 to-orange-500';
  if (score < 70) return 'from-orange-500 via-amber-500 to-yellow-400';
  if (score < 85) return 'from-yellow-500 via-lime-500 to-emerald-500';
  return 'from-emerald-500 via-cyan-500 to-blue-500';
}
function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function drawAiParagraphs(ctx: DocCtx, markdown: string): void {
  const { pdf } = ctx;
  const lines = markdown.split(/\r?\n/).slice(0, 120);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
    if (line.startsWith('#')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.replace(/^#+\s*/, ''), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    const printable = bullet ? `• ${bullet[1]}` : line.replace(/\*\*/g, '');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.3);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(printable, 182);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.2;
    }
  }
  ctx.y += 2;
}

async function exportPdf(payload: MitrePayload, aiAnalysis: string | null, filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'MITRE Coverage Command Center',
    customerName: payload.customerName,
    periodLabel: `${payload.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Coverage Overview', 'primary');
  const tiles: KpiTile[] = payload.cards.map((c) => ({
    value: String(c.value),
    label: c.label,
    tone: c.status === 'Critical' || c.status === 'High' ? 'danger'
      : c.status === 'Medium' ? 'warning'
      : c.status === 'Healthy' || c.status === 'Low' ? 'success'
      : 'default',
  }));
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: payload.score.value < 50 ? 'critical' : payload.score.value < 70 ? 'warning' : 'success',
    title: `Coverage Score: ${payload.score.value}/100 (${payload.score.level})`,
    body: payload.score.summary,
  });

  if (aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis', 'primary');
    drawAiParagraphs(ctx, aiAnalysis.slice(0, 3500));
  }

  drawSectionBanner(ctx, 'Top Coverage Gaps', 'orange');
  if (payload.gaps.length) {
    const gapRows: TableCell[][] = payload.gaps.slice(0, 20).map((g) => [
      severityCell(g.severity),
      text(g.tactic, 30),
      text(`${g.techniqueId} ${g.techniqueName}`, 50),
      text(g.recommendation, 140),
    ]);
    drawTable(ctx, {
      head: ['Severity', 'Tactic', 'Technique', 'Recommendation'],
      body: gapRows,
      columnStyles: {
        0: { cellWidth: 20, halign: 'center' },
        1: { cellWidth: 30 },
        2: { cellWidth: 42 },
        3: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No coverage gaps found for the selected lookback.');
  }

  drawSectionBanner(ctx, 'MITRE DEFEND / D3FEND Coverage', 'primary');
  if (payload.defend.length) {
    const defendRows: TableCell[][] = payload.defend.slice(0, 25).map((d) => [
      text(d.phase, 24),
      text(d.name, 40),
      d.status === 'covered' ? 'Covered' : d.status === 'partial' ? 'Partial' : 'Missing',
      `${d.coveragePercent}%`,
      text(d.recommendation, 130),
    ]);
    drawTable(ctx, {
      head: ['Phase', 'Capability', 'Status', 'Coverage', 'Recommendation'],
      body: defendRows,
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 40 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No DEFEND/D3FEND capability data available.');
  }

  drawSectionBanner(ctx, 'Top Analytic Rules', 'primary');
  if (payload.rules.length) {
    const ruleRows: TableCell[][] = payload.rules.slice(0, 25).map((r) => [
      r.enabled ? 'Enabled' : 'Disabled',
      text(r.ruleName, 40),
      text(r.techniques.join(', ') || 'No MITRE', 40),
      String(r.alertVolume),
      text(r.recommendation, 110),
    ]);
    drawTable(ctx, {
      head: ['Status', 'Rule Name', 'Techniques', 'Alerts', 'Recommendation'],
      body: ruleRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 40 },
        2: { cellWidth: 38 },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No analytic rules found.');
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

interface CoverageTabProps { initialFramework?: CoverageFramework }

export function CoverageTab({ initialFramework = 'attack' }: CoverageTabProps = {}) {
  const { canCapability } = useAuth();
  const { activeCustomerId, activeCustomer } = useCustomers();
  const customerId = activeCustomerId !== 'default' ? activeCustomerId : undefined;
  const customerName = activeCustomer?.name;
  const [lookback, setLookback] = useState<Lookback>('30d');
  const [payload, setPayload] = useState<MitrePayload | null>(null);
  const [selectedTactic, setSelectedTactic] = useState<string>('all');
  const [selectedTechnique, setSelectedTechnique] = useState<TechniqueCoverage | null>(null);
  const [selectedTableId, setSelectedTableId] = useState('attack_techniques');
  const [framework, setFramework] = useState<CoverageFramework>(initialFramework);
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('coverage', 'read');
  const canRefresh = canCapability('coverage', 'refresh');
  const canAi = canCapability('coverage', 'ai_analysis');
  const canExport = canCapability('coverage', 'export');

  async function load(refresh = false) {
    if (!customerId) return;
    setLoading(true); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=true' : ''}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `MITRE Coverage load failed (${resp.status})`);
      const data = parsed as MitrePayload;
      setPayload(data);
      setAiAnalysis(null);
      setSelectedTableId(data.tables[0]?.id ?? 'attack_techniques');
      setSelectedTechnique(data.attack.flatMap((g) => g.techniques).find((t) => t.status !== 'covered') ?? data.attack[0]?.techniques[0] ?? null);
      toast({ title: data.cacheHit ? 'MITRE Coverage loaded from cache' : 'MITRE Coverage refreshed', description: data.score.summary });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setLoading(false); }
  }

  useEffect(() => { setFramework(initialFramework); }, [initialFramework]);
  useEffect(() => { if (customerId && canRead) void load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

  async function generateAiAnalysis() {
    if (!customerId || !payload) return;
    setAiBusy(true); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/ai-analysis`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `AI analysis failed (${resp.status})`);
      setAiAnalysis(String(parsed.analysis ?? ''));
      toast({ title: parsed.fallback ? 'Fallback MITRE analysis generated' : 'MITRE AI analysis generated', description: parsed.model ?? 'AI analysis' });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setAiBusy(false); }
  }

  async function exportReport(format: 'html' | 'csv' | 'pdf') {
    if (!customerId || !payload) return;
    setExportBusy(format); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/export`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload, format, aiAnalysis }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      const base = String(parsed.filenameBase ?? `kloudsecure-mitre-coverage-${customerId}`);
      if (format === 'csv') downloadText(`${base}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      else if (format === 'html') downloadText(`${base}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      else await exportPdf(payload, aiAnalysis, base);
      toast({ title: `MITRE Coverage ${format.toUpperCase()} exported`, description: 'Export action audited.' });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setExportBusy(null); }
  }

  const selectedTable = payload?.tables.find((t) => t.id === selectedTableId) ?? payload?.tables[0] ?? null;
  const tableColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => (selectedTable?.columns ?? []).map((column) => ({ key: column, header: column, headerText: column, value: (row) => row[column], render: (row) => <span className="whitespace-pre-wrap break-words">{text(row[column], 500)}</span> })), [selectedTable]);
  const visibleAttack = useMemo(() => payload?.attack.filter((g) => selectedTactic === 'all' || g.tactic === selectedTactic) ?? [], [payload, selectedTactic]);

  if (!customerId) {
    return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">Select a customer tenant to load MITRE Coverage.</div>;
  }
  if (!canRead) {
    return <div className="rounded-2xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">Your role does not have MITRE Coverage read permission.</div>;
  }

  return (
    <div className="space-y-5 slide-in">
      <section className="rounded-3xl border border-violet-400/30 bg-gradient-to-br from-violet-950/80 via-indigo-950/70 to-slate-950 p-7 shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-5xl">
            <div className="text-[11px] font-black uppercase tracking-[0.45em] text-cyan-200">Detection Engineering Maturity</div>
            <h2 className="mt-3 text-3xl font-black text-white">MITRE Coverage Command Center</h2>
            <p className="mt-3 text-sm leading-6 text-blue-100">Expanded ATT&CK coverage, MITRE DEFEND/D3FEND defensive capability mapping, procedure-level analytics rule drilldowns, live alert activity and prioritized engineering recommendations for {customerName ?? customerId}.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <label className="text-xs font-bold text-blue-100">Lookback</label>
          <select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="rounded-xl border border-white/20 bg-slate-950 px-4 py-2 text-sm text-white">
            {(['24h', '7d', '30d', '60d', '90d'] as Lookback[]).map((v) => <option key={v} value={v}>{v === '24h' ? '24 hours' : v}</option>)}
          </select>
          <button onClick={() => void load(false)} disabled={loading} className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-bold text-white disabled:opacity-60">{loading ? 'Loading…' : 'Load'}</button>
          <button onClick={() => void load(true)} disabled={loading || !canRefresh} className="rounded-xl border border-white/20 px-5 py-2 text-sm font-bold text-white disabled:opacity-40">Refresh</button>
          <button onClick={() => void generateAiAnalysis()} disabled={!payload || aiBusy || !canAi} className="rounded-xl bg-purple-700 px-5 py-2 text-sm font-bold text-white disabled:opacity-40">{aiBusy ? 'Analyzing…' : 'AI Analysis'}</button>
          <button onClick={() => void exportReport('pdf')} disabled={!payload || !!exportBusy || !canExport} className="rounded-xl border border-white/20 px-5 py-2 text-sm font-bold text-white disabled:opacity-40">Export PDF</button>
          <button onClick={() => void exportReport('csv')} disabled={!payload || !!exportBusy || !canExport} className="rounded-xl border border-white/20 px-5 py-2 text-sm font-bold text-white disabled:opacity-40">Export CSV</button>
          <button onClick={() => void exportReport('html')} disabled={!payload || !!exportBusy || !canExport} className="rounded-xl border border-white/20 px-5 py-2 text-sm font-bold text-white disabled:opacity-40">Export HTML</button>
        </div>
        {payload && <div className="mt-4 text-xs text-blue-100">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'} · Cache expires {payload.expiresAt ? fmtTs(payload.expiresAt) : 'n/a'}</div>}
      </section>

      {error && <div className="rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">{error}</div>}

      {payload && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className={`rounded-2xl bg-gradient-to-br ${scoreGradient(payload.score.value)} p-5 text-white shadow-xl`}>
              <div className="text-xs font-black uppercase">Coverage Score</div>
              <div className="mt-3 text-5xl font-black">{payload.score.value}<span className="text-2xl">/100</span></div>
              <div className="mt-1 text-lg font-extrabold">{payload.score.level}</div>
              <p className="mt-4 text-sm text-white/90">{payload.score.summary}</p>
            </div>
            {payload.cards.slice(1).map((card) => (
              <button key={card.id} onClick={() => { if (card.id === 'defend_capabilities') setFramework('defend'); else if (card.id === 'analytics_rules') setFramework('rules'); else if (card.id === 'procedures') setFramework('procedures'); else setFramework('attack'); }} className="rounded-2xl border border-slate-700/80 bg-slate-900/70 p-5 text-left transition hover:border-blue-400/70 hover:bg-slate-800/80">
                <div className="flex items-center justify-between gap-3"><div className="text-xs font-black uppercase text-blue-200">{card.label}</div><span className={`rounded-full px-3 py-1 text-[11px] font-black ${riskPill(card.status)}`}>{card.status}</span></div>
                <div className="mt-4 text-3xl font-black text-white">{card.value}</div>
                <p className="mt-3 text-sm text-blue-100/80">{card.helper}</p>
              </button>
            ))}
          </section>

          {aiAnalysis && <section className="rounded-2xl border border-purple-300 bg-purple-50 p-5 dark:border-purple-500/40 dark:bg-purple-500/10"><h3 className="text-sm font-black text-purple-800 dark:text-purple-100">AI MITRE Coverage Analysis</h3><pre className="mt-3 whitespace-pre-wrap text-sm leading-6 text-purple-900 dark:text-purple-50">{aiAnalysis}</pre></section>}

          {payload.diagnostics.length > 0 && <section className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"><div className="font-black">Partial data warnings</div><ul className="mt-2 list-disc space-y-1 pl-5">{payload.diagnostics.map((d, i) => <li key={i}>{d}</li>)}</ul></section>}

          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h3 className="text-lg font-black text-foreground">Interactive MITRE Workspace</h3><p className="text-xs text-muted-foreground">Click a tactic, technique, DEFEND capability or rule to drill into procedure and analytic rule details.</p></div>
              <div className="flex flex-wrap gap-2">
                {(['attack', 'defend', 'rules', 'procedures', 'evidence'] as const).map((tab) => <button key={tab} onClick={() => setFramework(tab)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${framework === tab ? 'bg-blue-600 text-white' : 'border border-border text-muted-foreground'}`}>{tab === 'attack' ? 'ATT&CK Matrix' : tab === 'defend' ? 'DEFEND' : tab === 'rules' ? 'Rules' : tab === 'procedures' ? 'Procedures' : 'Evidence Tables'}</button>)}
              </div>
            </div>

            {framework === 'attack' && (
              <div className="mt-4 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => setSelectedTactic('all')} className={`rounded-full px-3 py-1.5 text-xs font-bold ${selectedTactic === 'all' ? 'bg-blue-600 text-white' : 'border border-border text-blue-100'}`}>All tactics</button>
                  {payload.attack.map((g) => <button key={g.tactic} onClick={() => setSelectedTactic(g.tactic)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${selectedTactic === g.tactic ? 'bg-blue-600 text-white' : 'border border-border text-blue-100'}`}>{g.tactic} · {g.covered}/{g.totalTechniques}</button>)}
                </div>
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
                  <div className="overflow-x-auto rounded-2xl border border-border bg-slate-950/40 p-4">
                    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(visibleAttack.length, 1), 7)}, minmax(220px, 1fr))` }}>
                      {visibleAttack.map((group) => (
                        <div key={group.tactic} className="min-w-[220px] rounded-xl border border-slate-700/80 bg-slate-900/60 p-3">
                          <button onClick={() => setSelectedTactic(group.tactic)} className="mb-3 w-full text-left"><div className="text-xs font-black uppercase tracking-widest text-cyan-200">{group.tactic}</div><div className="mt-1 text-[11px] text-slate-300">{group.covered} covered · {group.partial} partial · {group.missing} missing · alerts {group.alertVolume}</div></button>
                          <div className="space-y-2">
                            {group.techniques.map((t) => <button key={`${group.tactic}-${t.id}`} onClick={() => setSelectedTechnique(t)} className={`w-full rounded-lg border p-2 text-left text-[11px] transition hover:scale-[1.01] ${statusClass(t.status)} ${selectedTechnique?.id === t.id && selectedTechnique?.tactic === t.tactic ? 'ring-2 ring-blue-300' : ''}`}><div className="flex items-center justify-between gap-2"><span className="font-mono font-black">{t.id}</span><span className="text-[10px] font-bold uppercase">{t.status}</span></div><div className="mt-1 leading-tight">{t.name}</div><div className="mt-1 opacity-80">{t.enabledRules}/{t.totalRules} enabled · {t.alertVolume} alerts</div></button>)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <TechniqueDetail technique={selectedTechnique} />
                </div>
              </div>
            )}

            {framework === 'defend' && <DefendGrid defend={payload.defend} />}
            {framework === 'rules' && <RuleTable rules={payload.rules} />}
            {framework === 'procedures' && <ProcedureTable procedures={payload.procedures} />}
            {framework === 'evidence' && selectedTable && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2"><span className="text-xs font-bold text-muted-foreground">Evidence table</span>{payload.tables.map((t) => <button key={t.id} onClick={() => setSelectedTableId(t.id)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${selectedTableId === t.id ? 'bg-blue-600 text-white' : 'border border-border text-muted-foreground'}`}>{t.title} ({t.rows.length})</button>)}</div>
                {selectedTable.warnings.length > 0 && <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">{selectedTable.warnings.join(' · ')}</div>}
                <FilterableColumnTable columns={tableColumns} rows={selectedTable.rows} storageKey={`mitre-evidence-${selectedTable.id}`} rowKey={(_, idx) => idx} maxHeightClassName="max-h-[620px]" />
              </div>
            )}
          </section>

          <section className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4"><h3 className="text-base font-black text-foreground">Priority Coverage Recommendations</h3><div className="mt-3 space-y-3">{payload.recommendations.slice(0, 10).map((r, i) => <div key={`${r.title}-${i}`} className="rounded-xl border border-slate-700/80 bg-slate-900/60 p-3"><div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${riskPill(r.priority)}`}>{r.priority}</span><span className="text-sm font-bold text-white">{r.title}</span></div><p className="mt-2 text-xs leading-5 text-blue-100/80">{r.details}</p><div className="mt-2 text-[11px] text-muted-foreground">Owner: {r.owner}</div></div>)}</div></div>
            <div className="rounded-2xl border border-border bg-card p-4"><h3 className="text-base font-black text-foreground">Top ATT&CK Gaps</h3><FilterableColumnTable columns={gapColumns} rows={payload.gaps} storageKey="mitre-gap-table" rowKey={(row) => row.id} maxHeightClassName="max-h-[500px]" /></div>
          </section>
        </>
      )}
    </div>
  );
}


function oobPillClass(value: string): string {
  if (/AlreadyExistsDesiredState|Covered$|ReadyNoAction/.test(value)) return 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-100 dark:border-emerald-400/40';
  if (/WouldCreate|Deployable|ReadyForDisabledPlan|CoverageOpportunity/.test(value)) return 'bg-cyan-50 text-cyan-700 border-cyan-300 dark:bg-cyan-500/20 dark:text-cyan-100 dark:border-cyan-400/40';
  if (/Different|Drift|Review|Unavailable|EnabledDeploymentNeedsApproval/.test(value)) return 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-400/40';
  if (/Failed|Invalid/.test(value)) return 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-500/20 dark:text-rose-100 dark:border-rose-400/40';
  return 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-500/20 dark:text-slate-100 dark:border-slate-400/30';
}
function summaryNumber(summary: Record<string, number> | undefined, key: string): number { return Number(summary?.[key] ?? 0) || 0; }

function OobCoverageAssessmentPanel({ customerId, customerName, canExport }: { customerId: string; customerName: string; canExport: boolean }) {
  const [manifestText, setManifestText] = useState('');
  const [sourceName, setSourceName] = useState('manual-manifest.csv');
  const [runs, setRuns] = useState<OobRunListItem[]>([]);
  const [selectedRun, setSelectedRun] = useState<OobAssessmentRun | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);

  async function loadRuns(openRunId?: string) {
    setLoadingRuns(true); setPanelError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/oob-assessment/runs?customerId=${encodeURIComponent(customerId)}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Run list failed (${resp.status})`);
      const nextRuns = Array.isArray(parsed.runs) ? parsed.runs as OobRunListItem[] : [];
      setRuns(nextRuns);
      const target = openRunId || selectedRun?.runId || nextRuns[0]?.runId;
      if (target) await loadRun(target, false);
    } catch (err) { setPanelError(err instanceof Error ? err.message : String(err)); }
    finally { setLoadingRuns(false); }
  }

  async function loadRun(runId: string, showToast = true) {
    setPanelError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/oob-assessment/runs/${encodeURIComponent(runId)}?customerId=${encodeURIComponent(customerId)}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Run load failed (${resp.status})`);
      setSelectedRun(parsed as OobAssessmentRun);
      if (showToast) toast({ title: 'OOB assessment loaded', description: `${(parsed as OobAssessmentRun).rowCount} manifest row(s).` });
    } catch (err) { setPanelError(err instanceof Error ? err.message : String(err)); }
  }

  useEffect(() => { void loadRuns(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

  async function downloadTemplate(format: 'csv' | 'json') {
    setPanelError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/oob-assessment/template?format=${format}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Template download failed (${resp.status})`);
      if (format === 'json') downloadText(String(parsed.filename ?? 'kloudsecure-oob-coverage-manifest-template.json'), JSON.stringify(parsed.rows ?? [], null, 2), 'application/json;charset=utf-8');
      else downloadText(String(parsed.filename ?? 'kloudsecure-oob-coverage-manifest-template.csv'), String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      toast({ title: 'Manifest template downloaded', description: `${format.toUpperCase()} template ready for review.` });
    } catch (err) { setPanelError(err instanceof Error ? err.message : String(err)); }
  }

  async function assessManifest() {
    if (!manifestText.trim()) { setPanelError('Paste a CSV/JSON manifest or upload a template file first.'); return; }
    setAssessing(true); setPanelError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/oob-assessment/assess`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, sourceName, manifestText }),
      });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Assessment failed (${resp.status})`);
      const run = parsed as OobAssessmentRun;
      setSelectedRun(run);
      await loadRuns(run.runId);
      toast({ title: 'OOB coverage assessment completed', description: `${run.rowCount} row(s), no deployment performed.` });
    } catch (err) { setPanelError(err instanceof Error ? err.message : String(err)); }
    finally { setAssessing(false); }
  }

  async function exportRun(format: 'csv' | 'json') {
    if (!selectedRun) return;
    setExporting(format); setPanelError(null);
    try {
      const resp = await fetch(`${getApiBase()}/coverage/oob-assessment/runs/${encodeURIComponent(selectedRun.runId)}/export?customerId=${encodeURIComponent(customerId)}&format=${format}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      if (format === 'json') downloadText(String(parsed.filename ?? `kloudsecure-oob-coverage-assessment-${selectedRun.runId}.json`), JSON.stringify(parsed.payload ?? selectedRun, null, 2), 'application/json;charset=utf-8');
      else downloadText(String(parsed.filename ?? `kloudsecure-oob-coverage-assessment-${selectedRun.runId}.csv`), String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      toast({ title: `OOB assessment ${format.toUpperCase()} exported`, description: 'Export action audited.' });
    } catch (err) { setPanelError(err instanceof Error ? err.message : String(err)); }
    finally { setExporting(null); }
  }

  const columns = useMemo<FilterableColumnDef<OobAssessmentItem>[]>(() => [
    { key: 'rowIndex', header: 'Row', value: (r) => r.rowIndex, cellClassName: 'w-16 text-muted-foreground' },
    { key: 'ruleName', header: 'Rule', value: (r) => r.ruleName, render: (r) => <div><div className="font-bold text-foreground">{r.ruleName || '(missing rule name)'}</div><div className="mt-1 text-[11px] text-muted-foreground">{r.ruleId || 'no rule id'}</div></div> },
    { key: 'contentHubName', header: 'Content Hub', value: (r) => r.contentHubName || '(not specified)' },
    { key: 'deployDecision', header: 'Manifest Decision', value: (r) => r.deployDecision },
    { key: 'assessmentAction', header: 'ValidateOnly Action', value: (r) => r.assessmentAction, render: (r) => <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${oobPillClass(r.assessmentAction)}`}>{r.assessmentAction}</span> },
    { key: 'coverageState', header: 'Coverage State', value: (r) => r.coverageState, render: (r) => <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${oobPillClass(r.coverageState)}`}>{r.coverageState}</span> },
    { key: 'readinessState', header: 'Readiness', value: (r) => r.readinessState, render: (r) => <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${oobPillClass(r.readinessState)}`}>{r.readinessState}</span> },
    { key: 'existingRuleName', header: 'Existing Rule', value: (r) => r.existingRuleFound ? `${r.existingRuleName} (${r.existingEnabledState})` : 'Not found', render: (r) => <span className={r.existingRuleFound ? 'text-emerald-700 dark:text-emerald-100' : 'text-muted-foreground'}>{r.existingRuleFound ? `${r.existingRuleName || 'Existing rule'} · ${r.existingEnabledState}` : 'Not found'}</span> },
    { key: 'templateSource', header: 'Source', value: (r) => r.templateSource },
    { key: 'reason', header: 'Reason', value: (r) => r.reason, render: (r) => <span className="whitespace-pre-wrap break-words text-foreground/85">{r.reason}</span> },
  ], []);

  return (
    <div className="mt-4 space-y-4">
      <section className="rounded-2xl border border-cyan-500/30 bg-slate-950 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-black text-white">AnalyticsOps</h3>
            <p className="mt-1 text-sm text-blue-100/80">Manifest-driven ValidateOnly assessment for {customerName}. This patch stores assessment runs and never deploys or changes Sentinel analytics rules.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => void downloadTemplate('csv')} className="rounded-xl border border-cyan-400/40 px-3 py-2 text-xs font-black text-cyan-100">Download CSV Template</button>
            <button onClick={() => void downloadTemplate('json')} className="rounded-xl border border-cyan-400/40 px-3 py-2 text-xs font-black text-cyan-100">Download JSON Template</button>
          </div>
        </div>
        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <input value={sourceName} onChange={(e) => setSourceName(e.target.value)} className="min-w-[240px] rounded-xl border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white" placeholder="manifest source name" />
              <label className="cursor-pointer rounded-xl border border-white/15 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/5">
                Upload CSV/JSON
                <input className="hidden" type="file" accept=".csv,.txt,.json" onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setSourceName(file.name);
                  const reader = new FileReader();
                  reader.onload = () => setManifestText(String(reader.result ?? ''));
                  reader.onerror = () => setPanelError('Failed to read selected manifest file.');
                  reader.readAsText(file);
                }} />
              </label>
              <button onClick={() => void assessManifest()} disabled={assessing || !manifestText.trim()} className="rounded-xl bg-cyan-500 px-4 py-2 text-xs font-black text-slate-950 disabled:opacity-50">{assessing ? 'Assessing…' : 'Run ValidateOnly Assessment'}</button>
            </div>
            <textarea value={manifestText} onChange={(e) => setManifestText(e.target.value)} className="h-52 w-full rounded-2xl border border-white/15 bg-slate-950 p-3 font-mono text-xs text-blue-100 outline-none focus:border-cyan-400/60" placeholder="Paste CSV or JSON rows here. XLSX users can export to CSV or paste an Excel-to-JSON row array." />
            <div className="rounded-xl border border-blue-400/20 bg-slate-950/60 p-3 text-xs leading-5 text-blue-100/75">Supported columns: RuleName, RuleId, ContentHubName, Deploy, RepoPath. Deploy values such as <b>Yes</b>, <b>Yes but in disabled state</b>, <b>ValidateOnly</b> and <b>No</b> are normalized to assessment actions.</div>
          </div>
          <div className="rounded-2xl border border-border bg-slate-950/60 p-4">
            <div className="flex items-center justify-between gap-2"><h4 className="text-sm font-black text-white">Recent assessment runs</h4><button onClick={() => void loadRuns()} disabled={loadingRuns} className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-bold text-blue-100">Refresh</button></div>
            <div className="mt-3 max-h-64 space-y-2 overflow-auto pr-1">
              {runs.length === 0 && <div className="text-xs text-muted-foreground">No stored assessment runs yet.</div>}
              {runs.map((run) => <button key={run.runId} onClick={() => void loadRun(run.runId)} className={`w-full rounded-xl border p-3 text-left text-xs ${selectedRun?.runId === run.runId ? 'border-cyan-400/70 bg-cyan-500/10' : 'border-slate-700 bg-slate-900/60'}`}><div className="font-black text-white">{run.runId}</div><div className="mt-1 text-blue-100/75">{run.rowCount} rows · {fmtTs(run.generatedAt)}</div><div className="mt-1 text-muted-foreground">Covered {summaryNumber(run.summary, 'Covered')} · Opportunities {summaryNumber(run.summary, 'CoverageOpportunity')} · Review {summaryNumber(run.summary, 'ReviewRequired')}</div></button>)}
            </div>
          </div>
        </div>
      </section>

      {panelError && <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">{panelError}</div>}

      {selectedRun && (
        <section className="space-y-4 rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h3 className="text-lg font-black text-foreground">Assessment Results · {selectedRun.runId}</h3><p className="mt-1 text-xs text-muted-foreground">{selectedRun.sourceName} · {selectedRun.rowCount} rows · generated {fmtTs(selectedRun.generatedAt)} · no deployment performed</p></div>
            <div className="flex flex-wrap gap-2"><button onClick={() => void exportRun('csv')} disabled={!canExport || !!exporting} className="rounded-xl border border-border px-3 py-2 text-xs font-bold text-foreground disabled:opacity-40">Export CSV</button><button onClick={() => void exportRun('json')} disabled={!canExport || !!exporting} className="rounded-xl border border-border px-3 py-2 text-xs font-bold text-foreground disabled:opacity-40">Export JSON</button></div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <MiniStat label="Manifest Rows" value={selectedRun.rowCount} />
            <MiniStat label="Covered" value={summaryNumber(selectedRun.summary, 'Covered')} />
            <MiniStat label="Opportunities" value={summaryNumber(selectedRun.summary, 'CoverageOpportunity')} />
            <MiniStat label="Review Required" value={summaryNumber(selectedRun.summary, 'ReviewRequired')} />
            <MiniStat label="Skipped" value={summaryNumber(selectedRun.summary, 'Skipped')} />
          </div>
          {selectedRun.diagnostics.length > 0 && <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"><div className="font-black">Assessment diagnostics</div><ul className="mt-2 list-disc space-y-1 pl-5">{selectedRun.diagnostics.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
          <FilterableColumnTable<OobAssessmentItem> columns={columns} rows={selectedRun.items} storageKey={`oob-coverage-assessment-${selectedRun.runId}`} rowKey={(row) => row.id ?? row.rowIndex} maxHeightClassName="max-h-[720px]" paginationEnabled defaultPageSize={50} pageSizeOptions={[25, 50, 100, 250]} />
        </section>
      )}
    </div>
  );
}

function TechniqueDetail({ technique }: { technique: TechniqueCoverage | null }) {
  if (!technique) return <div className="rounded-2xl border border-border bg-slate-900/60 p-4 text-sm text-muted-foreground">Select a technique to view analytic rules, procedures and DEFEND mapping.</div>;
  return <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4"><div className="flex items-start justify-between gap-3"><div><div className="text-xs font-black uppercase tracking-widest text-cyan-200">{technique.tactic}</div><h3 className="mt-1 text-xl font-black text-white">{technique.id} · {technique.name}</h3></div><span className={`rounded-full px-3 py-1 text-xs font-black ${statusPill(technique.status)}`}>{technique.status}</span></div><div className="mt-4 grid grid-cols-2 gap-3 text-center text-xs"><MiniStat label="Enabled Rules" value={technique.enabledRules} /><MiniStat label="Total Rules" value={technique.totalRules} /><MiniStat label="Alert Volume" value={technique.alertVolume} /><MiniStat label="Incidents" value={technique.incidentVolume} /></div><div className="mt-4 text-sm text-blue-100">{technique.recommendation}</div><div className="mt-4"><div className="text-xs font-black uppercase text-blue-200">DEFEND capabilities</div><div className="mt-2 flex flex-wrap gap-2">{technique.defendCapabilities.map((d) => <span key={d} className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[11px] text-cyan-100">{d}</span>)}</div></div><div className="mt-4"><div className="text-xs font-black uppercase text-blue-200">Analytics rules</div><div className="mt-2 space-y-2">{technique.procedures.length ? technique.procedures.map((p) => <div key={p.id} className="rounded-xl border border-slate-700 bg-slate-950/60 p-3"><div className="flex items-center justify-between gap-2"><div className="text-sm font-bold text-white">{p.ruleName}</div><span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${p.enabled ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-white'}`}>{p.enabled ? 'Enabled' : 'Disabled'}</span></div><p className="mt-2 text-xs leading-5 text-blue-100/80">{p.procedureText}</p><div className="mt-2 text-[11px] text-muted-foreground">Tables: {p.dataSources.join(', ') || 'not parsed'} · Entities: {p.entityMappings.join(', ') || 'not mapped'} · Severity: {p.severity}</div></div>) : <div className="text-xs text-muted-foreground">No analytic rule mapped to this technique.</div>}</div></div></div>;
}
function MiniStat({ label, value }: { label: string; value: string | number }) { return <div className="rounded-xl border border-white/10 bg-slate-950/50 p-3"><div className="text-xl font-black text-white">{value}</div><div className="text-[10px] font-bold uppercase text-muted-foreground">{label}</div></div>; }
function DefendGrid({ defend }: { defend: DefendCapability[] }) { return <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">{defend.map((d) => <div key={d.id} className={`rounded-2xl border p-4 ${statusClass(d.status)}`}><div className="flex items-start justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-widest opacity-80">{d.phase} · {d.id}</div><div className="mt-1 text-base font-black">{d.name}</div></div><span className={`rounded-full px-2 py-1 text-[10px] font-black ${statusPill(d.status)}`}>{d.coveragePercent}%</span></div><p className="mt-3 text-xs leading-5 opacity-90">{d.description}</p><div className="mt-3 text-xs">{d.coveredTechniques}/{d.totalTechniques} mapped ATT&CK techniques covered</div><div className="mt-2 text-[11px] opacity-80">Rules: {d.supportingRules.slice(0, 4).join('; ') || 'No supporting rule'}</div><p className="mt-3 text-xs font-semibold">{d.recommendation}</p></div>)}</div>; }

const ruleColumns: FilterableColumnDef<RuleSummary>[] = [
  { key: 'ruleName', header: 'Rule', value: (r) => r.ruleName, render: (r) => <span className="font-bold text-white">{r.ruleName}</span> },
  { key: 'enabled', header: 'State', value: (r) => r.enabled ? 'Enabled' : 'Disabled', render: (r) => <span className={`rounded-full px-2 py-1 text-[10px] font-black ${r.enabled ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-white'}`}>{r.enabled ? 'Enabled' : 'Disabled'}</span> },
  { key: 'severity', header: 'Severity', value: (r) => r.severity },
  { key: 'techniques', header: 'Techniques', value: (r) => r.techniques.join(', ') },
  { key: 'alerts', header: 'Alerts', value: (r) => r.alertVolume },
  { key: 'incidents', header: 'Incidents', value: (r) => r.incidentVolume },
  { key: 'dataSources', header: 'Data Sources', value: (r) => r.dataSources.join(', ') },
  { key: 'gaps', header: 'Gaps', value: (r) => r.gaps.join(', ') },
  { key: 'recommendation', header: 'Recommendation', value: (r) => r.recommendation, render: (r) => <span className="whitespace-pre-wrap">{r.recommendation}</span> },
];
function RuleTable({ rules }: { rules: RuleSummary[] }) { return <div className="mt-4"><FilterableColumnTable columns={ruleColumns} rows={rules} storageKey="mitre-rules-table" rowKey={(r) => r.id} maxHeightClassName="max-h-[620px]" /></div>; }

const procedureColumns: FilterableColumnDef<AttackProcedure>[] = [
  { key: 'tactic', header: 'Tactic', value: (r) => r.tactic },
  { key: 'technique', header: 'Technique', value: (r) => `${r.techniqueId} ${r.techniqueName}` },
  { key: 'ruleName', header: 'Rule', value: (r) => r.ruleName, render: (r) => <span className="font-bold text-white">{r.ruleName}</span> },
  { key: 'enabled', header: 'State', value: (r) => r.enabled ? 'Enabled' : 'Disabled' },
  { key: 'severity', header: 'Severity', value: (r) => r.severity },
  { key: 'dataSources', header: 'Data Sources', value: (r) => r.dataSources.join(', ') },
  { key: 'entityMappings', header: 'Entities', value: (r) => r.entityMappings.join(', ') },
  { key: 'procedureText', header: 'Procedure', value: (r) => r.procedureText, render: (r) => <span className="whitespace-pre-wrap">{r.procedureText}</span> },
  { key: 'recommendation', header: 'Recommendation', value: (r) => r.recommendation, render: (r) => <span className="whitespace-pre-wrap">{r.recommendation}</span> },
];
function ProcedureTable({ procedures }: { procedures: AttackProcedure[] }) { return <div className="mt-4"><FilterableColumnTable columns={procedureColumns} rows={procedures} storageKey="mitre-procedure-table" rowKey={(r) => r.id} maxHeightClassName="max-h-[620px]" /></div>; }

const gapColumns: FilterableColumnDef<MitrePayload['gaps'][number]>[] = [
  { key: 'severity', header: 'Priority', value: (r) => r.severity, render: (r) => <span className={`rounded-full px-2 py-1 text-[10px] font-black ${riskPill(r.severity)}`}>{r.severity}</span> },
  { key: 'tactic', header: 'Tactic', value: (r) => r.tactic },
  { key: 'techniqueId', header: 'Technique', value: (r) => r.techniqueId },
  { key: 'techniqueName', header: 'Name', value: (r) => r.techniqueName },
  { key: 'defendHint', header: 'DEFEND Focus', value: (r) => r.defendHint },
  { key: 'recommendation', header: 'Recommendation', value: (r) => r.recommendation, render: (r) => <span className="whitespace-pre-wrap">{r.recommendation}</span> },
];
